import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface Leaderboard {
  id: string;
  name: string;
  type: 'global' | 'regional' | 'venue';
  category: 'total_points' | 'wins' | 'matches_played' | 'charity_donations' | 'streak';
  timeframe: 'daily' | 'weekly' | 'monthly' | 'all_time' | 'custom';
  topN: number;
  enabled: boolean;
  region?: string;
  venueId?: string;
  createdAt: Date;
}

const LeaderboardList: React.FC = () => {
  const { t } = useTranslation();
  const [leaderboards, setLeaderboards] = useState<Leaderboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Leaderboard | null>(null);
  const [form, setForm] = useState({
    name: '',
    type: 'global' as Leaderboard['type'],
    category: 'total_points' as Leaderboard['category'],
    timeframe: 'all_time' as Leaderboard['timeframe'],
    topN: 100,
    enabled: true,
    region: '',
    venueId: ''
  });

  useEffect(() => {
    loadLeaderboards();
  }, []);

  const loadLeaderboards = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'leaderboards'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      })) as Leaderboard[];
      setLeaderboards(data);
    } catch (error) {
      console.error('Error loading leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...form,
        updatedAt: Timestamp.now()
      };

      if (editing) {
        await updateDoc(doc(db, 'leaderboards', editing.id), data);
      } else {
        await addDoc(collection(db, 'leaderboards'), {
          ...data,
          createdAt: Timestamp.now()
        });
      }

      setShowModal(false);
      setEditing(null);
      resetForm();
      loadLeaderboards();
    } catch (error) {
      console.error('Error saving leaderboard:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('leaderboard.confirmDeleteLeaderboard'))) {
      try {
        await deleteDoc(doc(db, 'leaderboards', id));
        loadLeaderboards();
      } catch (error) {
        console.error('Error deleting leaderboard:', error);
      }
    }
  };

  const handleEdit = (leaderboard: Leaderboard) => {
    setEditing(leaderboard);
    setForm({
      name: leaderboard.name,
      type: leaderboard.type,
      category: leaderboard.category,
      timeframe: leaderboard.timeframe,
      topN: leaderboard.topN,
      enabled: leaderboard.enabled,
      region: leaderboard.region || '',
      venueId: leaderboard.venueId || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      type: 'global',
      category: 'total_points',
      timeframe: 'all_time',
      topN: 100,
      enabled: true,
      region: '',
      venueId: ''
    });
  };

  const getTypeIcon = (type: Leaderboard['type']) => {
    switch (type) {
      case 'global': return '🌍';
      case 'regional': return '📍';
      case 'venue': return '🏢';
      default: return '📊';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">{t('leaderboard.leaderboardManagement')}</h3>
        <button
          onClick={() => {
            resetForm();
            setEditing(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('leaderboard.newLeaderboard')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {leaderboards.map(leaderboard => (
          <div key={leaderboard.id} className="card-dark p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <span className="text-3xl">{getTypeIcon(leaderboard.type)}</span>
                <h4 className="text-xl font-bold text-white">{leaderboard.name}</h4>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                leaderboard.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
              }`}>
                {leaderboard.enabled ? t('leaderboard.enabled') : t('leaderboard.disabled')}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B8B8C3]">Típus:</span>
                <span className="text-white font-semibold">{t(`leaderboard.type.${leaderboard.type}`)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B8B8C3]">Kategória:</span>
                <span className="text-white">{t(`leaderboard.category.${leaderboard.category}`)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B8B8C3]">Időkeret:</span>
                <span className="text-white">{t(`leaderboard.timeframe.${leaderboard.timeframe}`)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#B8B8C3]">{t('leaderboard.topN')}:</span>
                <span className="text-cyan-400 font-bold">{leaderboard.topN}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleEdit(leaderboard)}
                className="flex-1 btn-secondary text-sm"
              >
                {t('common.edit')}
              </button>
              <button
                onClick={() => handleDelete(leaderboard.id)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {leaderboards.length === 0 && (
        <div className="card-dark p-12 text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('leaderboard.noLeaderboards')}</h3>
          <p className="text-[#B8B8C3] mb-4">{t('leaderboard.noLeaderboardsDesc')}</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="btn-primary"
          >
            {t('leaderboard.createFirstLeaderboard')}
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editing ? t('leaderboard.editLeaderboard') : t('leaderboard.newLeaderboard')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('leaderboard.leaderboardName')}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('leaderboard.leaderboardNamePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Típus
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as Leaderboard['type'] })}
                    className="input-dark w-full"
                  >
                    <option value="global">{t('leaderboard.type.global')}</option>
                    <option value="regional">{t('leaderboard.type.regional')}</option>
                    <option value="venue">{t('leaderboard.type.venue')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Kategória
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value as Leaderboard['category'] })}
                    className="input-dark w-full"
                  >
                    <option value="total_points">{t('leaderboard.category.total_points')}</option>
                    <option value="wins">{t('leaderboard.category.wins')}</option>
                    <option value="matches_played">{t('leaderboard.category.matches_played')}</option>
                    <option value="charity_donations">{t('leaderboard.category.charity_donations')}</option>
                    <option value="streak">{t('leaderboard.category.streak')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    Időkeret
                  </label>
                  <select
                    value={form.timeframe}
                    onChange={(e) => setForm({ ...form, timeframe: e.target.value as Leaderboard['timeframe'] })}
                    className="input-dark w-full"
                  >
                    <option value="daily">{t('leaderboard.timeframe.daily')}</option>
                    <option value="weekly">{t('leaderboard.timeframe.weekly')}</option>
                    <option value="monthly">{t('leaderboard.timeframe.monthly')}</option>
                    <option value="all_time">{t('leaderboard.timeframe.all_time')}</option>
                    <option value="custom">{t('leaderboard.timeframe.custom')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('leaderboard.topN')}
                  </label>
                  <input
                    type="number"
                    value={form.topN}
                    onChange={(e) => setForm({ ...form, topN: Number(e.target.value) })}
                    className="input-dark w-full"
                    min="10"
                    max="1000"
                  />
                </div>
              </div>

              {form.type === 'regional' && (
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('common.region')}
                  </label>
                  <input
                    type="text"
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                    className="input-dark w-full"
                    placeholder="e.g., Budapest, Hungary"
                  />
                </div>
              )}

              {form.type === 'venue' && (
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('common.venue')}
                  </label>
                  <input
                    type="text"
                    value={form.venueId}
                    onChange={(e) => setForm({ ...form, venueId: e.target.value })}
                    className="input-dark w-full"
                    placeholder="Venue ID"
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="enabled" className="text-sm text-[#B8B8C3]">
                  {t('leaderboard.enableLeaderboard')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                  resetForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardList;
